// react
import { useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled, { css } from 'styled-components';

// mux
import MuxPlayer from '@mux/mux-player-react';

// portabletext
import { PortableText } from '@portabletext/react';

// store
import { Content as ContentType } from '@karehero/models';

// utils
import { buildFileUrl, buildImageUrl } from '@/utils/sanity';

const ptComponents = {
  marks: {
    link: (props: any) => (
      <StyledLink
        href={props.value.href}
        target='_blank'
        rel='noopener noreferrer'
      >
        {props.children}
      </StyledLink>
    ),
  },
  block: {
    h1: (props: any) => <Heading1>{props.children}</Heading1>,
    h2: (props: any) => <Heading2>{props.children}</Heading2>,
    h3: (props: any) => <Heading3>{props.children}</Heading3>,
    h4: (props: any) => <Heading4>{props.children}</Heading4>,
    h5: (props: any) => <Heading5>{props.children}</Heading5>,
    h6: (props: any) => <Heading6>{props.children}</Heading6>,
  },
  types: {
    image: (props: any) => (
      <BlockImage>
        <img src={buildImageUrl(props?.value?.asset?.url)} alt='related' />
      </BlockImage>
    ),
    'mux.video': (props: any) => (
      <StyledMuxPlayer playbackId={props?.value?.asset?.playbackId} />
    ),
    file: (props: any) => (
      <a href={buildFileUrl(props?.value?.asset?._ref)} target='_blank'>
        {props?.value?.title}
      </a>
    ),
  },
  list: {
    number: (props: any) => {
      let type: '1' | 'I' | 'i' | 'A' | 'a' = '1';
      let parentValue: number | undefined = undefined;
      const level = props.value.level;

      parentValue = 10;

      return (
        <OL type={type} $level={level} $parentValue={parentValue}>
          {props.children}
        </OL>
      );
    },
  },
};

interface OLProps {
  $level?: number;
  $parentValue?: number;
}
const OL = styled.ol<OLProps>`
  ${(props) => {
    switch (props.$level) {
      case 1:
      case 2:
        return css`
          padding-left: 0;
        `;
      default:
        return css`
          padding-left: ${props.theme.spacing[20]};
        `;
    }
  }}

  list-style-type: none;
  counter-reset: section;

  ${(props) => {
    let type: string | undefined;
    let font = props.theme.typography.body.md.regular;
    let content: string = '';
    let beforeWidth = props.theme.spacing[32];

    switch (props.$level) {
      case 1:
        font = props.theme.typography.body.md.bold;
        content = `counter(section) ". "`;
        break;
      case 2:
        content = `counters(section, ".") ' '`;
        break;
      case 3:
        content = `'('counter(section, lower-alpha)') '`;
        break;
      case 4:
        type = 'lower-roman';
        break;
      default:
        font = props.theme.typography.body.md.regular;
        type = undefined;
        content = '';
        break;
    }

    return css`
      & > li {
        margin: ${(props) => props.theme.spacing[8]} 0;
        list-style-type: ${type};
        position: relative;
        padding-left: ${beforeWidth};
      }
      & > li:before {
        counter-increment: section;
        content: ${content};
        font: ${font};

        display: inline-block;
        position: absolute;
        left: -${props.theme.spacing[8]};
        width: ${beforeWidth};
        text-align: right;
      }
    `;
  }}}
`;

export const SanityContent = ({ content }: SanityContentProps) => {
  // hooks
  const location = useLocation();

  // memos
  const ptDynamicComponents = useMemo(() => {
    return {
      ...ptComponents,
      marks: {
        ...ptComponents.marks,
        internalLink: (props: any) => (
          <StyledInternalLink to={`/library/${props.value.slug.current}`}>
            {props.children}
          </StyledInternalLink>
        ),
      },
    };
  }, [location]);

  return (
    <Wrapper>
      {content && (
        <PortableText value={content} components={ptDynamicComponents} />
      )}
    </Wrapper>
  );
};

const Wrapper = styled.div`
  font: ${(props) => props.theme.typography.body.md.regular};
  color: ${(props) => props.theme.color.neutral[90]};
`;

const StyledMuxPlayer = styled(MuxPlayer)`
  aspect-ratio: 16 / 9;
  border-radius: 8px;
  overflow: hidden;
`;

const Heading1 = styled.h1`
  font: ${(props) => props.theme.typography.heading['7xl']};
  color: ${(props) => props.theme.color.neutral[100]};
`;

const Heading2 = styled.h2`
  font: ${(props) => props.theme.typography.body.xxl.medium};
  color: ${(props) => props.theme.color.neutral[100]};
`;

const Heading3 = styled.h3`
  font: ${(props) => props.theme.typography.body.xl.medium};
  color: ${(props) => props.theme.color.neutral[100]};
`;

const Heading4 = styled.h4`
  font: ${(props) => props.theme.typography.body.lg.semibold};
  color: ${(props) => props.theme.color.neutral[90]};
`;

const Heading5 = styled.h5`
  font: ${(props) => props.theme.typography.body.md.semibold};
  color: ${(props) => props.theme.color.neutral[90]};
`;

const Heading6 = styled.h6`
  font: ${(props) => props.theme.typography.body.sm.semibold};
  color: ${(props) => props.theme.color.neutral[90]};
`;

const StyledLink = styled.a`
  color: ${(props) => props.theme.color.primary[80]};
  font: ${(props) => props.theme.typography.body.md.medium};
  text-decoration: underline;
`;

const StyledInternalLink = styled(Link)`
  color: ${(props) => props.theme.color.primary[80]};
  font: ${(props) => props.theme.typography.body.md.medium};
  text-decoration: underline;
`;

const BlockImage = styled.div`
  display: flex;
  border-radius: 8px;
  overflow: hidden;
  margin-top: ${(props) => props.theme.spacing[16]};
  margin-bottom: ${(props) => props.theme.spacing[36]};
  & > img {
    width: 100%;
    object-fit: cover;
    user-select: none;
    user-drag: none;
    -webkit-user-drag: none;
    -moz-user-select: none;
    -webkit-user-select: none;
    -ms-user-select: none;
  }
`;

interface SanityContentProps {
  content?: ContentType[];
}

SanityContent.displayName = 'SanityContent';
export default SanityContent;
